import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CreditCard, Lock, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { coursesAPI } from '../../services/api';

const PaymentCheckout = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const courseId = location.state?.courseId;
    const courseName = location.state?.courseName || 'Selected Course';
    const coursePrice = location.state?.coursePrice || 0;

    useEffect(() => {
        if (courseId) {
            fetchCourseDetails();
        }
    }, [courseId]);

    useEffect(() => {
        // Load Razorpay script
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const fetchCourseDetails = async () => {
        try {
            const response = await coursesAPI.getCourse(courseId);
            setCourse(response.data);
        } catch (err) {
            console.error('Failed to fetch course details:', err);
        }
    };

    const handlePayment = async () => {
        setProcessing(true);
        setError('');

        try {
            // Get Razorpay order ID from backend
            const orderResponse = await fetch('http://localhost:8000/api/payments/create-order', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    amount: coursePrice,
                    course_id: courseId
                })
            });

            const orderData = await orderResponse.json();

            if (!orderData.success) {
                throw new Error(orderData.message || 'Failed to create order');
            }

            // Initialize Razorpay
            const options = {
                key: process.env.REACT_APP_RAZORPAY_KEY || 'rzp_test_demo',
                amount: orderData.amount,
                currency: 'INR',
                name: 'ARIVUNEST',
                description: courseName,
                order_id: orderData.order_id,
                handler: async function (response) {
                    // Verify payment
                    try {
                        const verifyResponse = await fetch('http://localhost:8000/api/payments/verify', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            body: JSON.stringify({
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                course_id: courseId
                            })
                        });

                        const verifyData = await verifyResponse.json();

                        if (verifyData.success) {
                            setSuccess(true);
                            setTimeout(() => {
                                navigate(`/courses/${courseId}/learn`);
                            }, 3000);
                        } else {
                            setError('Payment verification failed');
                        }
                    } catch (err) {
                        setError('Payment verification failed: ' + err.message);
                    } finally {
                        setProcessing(false);
                    }
                },
                prefill: {
                    name: JSON.parse(localStorage.getItem('user'))?.full_name || '',
                    email: JSON.parse(localStorage.getItem('user'))?.email || ''
                },
                theme: {
                    color: '#27B05B'
                },
                modal: {
                    ondismiss: function() {
                        setProcessing(false);
                        setError('Payment cancelled');
                    }
                }
            };

            const razorpay = new window.Razorpay(options);
            razorpay.open();

        } catch (err) {
            setError(err.message || 'Failed to initiate payment');
            setProcessing(false);
        }
    };

    if (success) {
        return (
            <div style={styles.container}>
                <div style={styles.successCard}>
                    <CheckCircle size={80} color="#27B05B" />
                    <h2 style={styles.successTitle}>Payment Successful!</h2>
                    <p style={styles.successMessage}>
                        Your enrollment in "{courseName}" has been confirmed.
                    </p>
                    <p style={styles.redirectMessage}>
                        Redirecting to course...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <button onClick={() => navigate(-1)} style={styles.backBtn}>
                <ArrowLeft size={20} />
                Back
            </button>

            <div style={styles.content}>
                <div style={styles.leftSection}>
                    <h1 style={styles.title}>Checkout</h1>
                    
                    <div style={styles.orderSummary}>
                        <h3 style={styles.sectionTitle}>Order Summary</h3>
                        <div style={styles.courseInfo}>
                            <div style={styles.courseName}>{courseName}</div>
                            <div style={styles.coursePrice}>₹{coursePrice}</div>
                        </div>
                        
                        <div style={styles.divider}></div>
                        
                        <div style={styles.totalSection}>
                            <span style={styles.totalLabel}>Total Amount</span>
                            <span style={styles.totalAmount}>₹{coursePrice}</span>
                        </div>
                    </div>

                    <div style={styles.securityNote}>
                        <Lock size={18} color="#27B05B" />
                        <span>Secure payment powered by Razorpay</span>
                    </div>
                </div>

                <div style={styles.rightSection}>
                    <div style={styles.paymentCard}>
                        <div style={styles.cardHeader}>
                            <CreditCard size={24} color="#27B05B" />
                            <h3 style={styles.cardTitle}>Payment Details</h3>
                        </div>

                        {error && (
                            <div style={styles.errorAlert}>
                                <XCircle size={20} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div style={styles.paymentInfo}>
                            <p style={styles.infoText}>
                                Click the button below to proceed with secure payment through Razorpay.
                            </p>
                            
                            <div style={styles.features}>
                                <div style={styles.feature}>
                                    <CheckCircle size={18} color="#27B05B" />
                                    <span>Lifetime access to course content</span>
                                </div>
                                <div style={styles.feature}>
                                    <CheckCircle size={18} color="#27B05B" />
                                    <span>Certificate of completion</span>
                                </div>
                                <div style={styles.feature}>
                                    <CheckCircle size={18} color="#27B05B" />
                                    <span>30-day money-back guarantee</span>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={handlePayment}
                            disabled={processing}
                            style={{
                                ...styles.payButton,
                                ...(processing ? styles.payButtonDisabled : {})
                            }}
                        >
                            {processing ? (
                                <>
                                    <div style={styles.spinner}></div>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <Lock size={20} />
                                    Pay ₹{coursePrice}
                                </>
                            )}
                        </button>

                        <p style={styles.disclaimer}>
                            By completing this purchase, you agree to our{' '}
                            <a href="/terms" style={styles.link}>Terms of Service</a>{' '}
                            and{' '}
                            <a href="/refund-policy" style={styles.link}>Refund Policy</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '40px 20px',
        minHeight: '100vh'
    },
    backBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: 'white',
        border: '2px solid #E0E0E0',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#666',
        cursor: 'pointer',
        marginBottom: '30px',
        transition: 'all 0.3s ease'
    },
    content: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '40px',
        '@media (max-width: 768px)': {
            gridTemplateColumns: '1fr'
        }
    },
    leftSection: {},
    rightSection: {},
    title: {
        fontSize: '36px',
        fontWeight: '700',
        color: '#2C3E50',
        marginBottom: '30px'
    },
    orderSummary: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '20px'
    },
    sectionTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#2C3E50',
        marginBottom: '20px'
    },
    courseInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '15px 0'
    },
    courseName: {
        fontSize: '16px',
        color: '#2C3E50',
        fontWeight: '500'
    },
    coursePrice: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#27B05B'
    },
    divider: {
        height: '1px',
        backgroundColor: '#E0E0E0',
        margin: '20px 0'
    },
    totalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 0'
    },
    totalLabel: {
        fontSize: '18px',
        fontWeight: '600',
        color: '#2C3E50'
    },
    totalAmount: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#27B05B'
    },
    securityNote: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '15px',
        backgroundColor: '#F0F9F4',
        borderRadius: '8px',
        fontSize: '14px',
        color: '#27B05B'
    },
    paymentCard: {
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '30px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginBottom: '25px'
    },
    cardTitle: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#2C3E50',
        margin: 0
    },
    errorAlert: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '15px',
        backgroundColor: '#FFE6E6',
        color: '#E74C3C',
        borderRadius: '8px',
        marginBottom: '20px',
        fontSize: '14px'
    },
    paymentInfo: {
        marginBottom: '25px'
    },
    infoText: {
        fontSize: '14px',
        color: '#666',
        lineHeight: '1.6',
        marginBottom: '20px'
    },
    features: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    feature: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '14px',
        color: '#2C3E50'
    },
    payButton: {
        width: '100%',
        padding: '18px',
        backgroundColor: '#27B05B',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '18px',
        fontWeight: '700',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        transition: 'all 0.3s ease',
        marginBottom: '15px'
    },
    payButtonDisabled: {
        backgroundColor: '#ccc',
        cursor: 'not-allowed'
    },
    disclaimer: {
        fontSize: '12px',
        color: '#999',
        textAlign: 'center',
        lineHeight: '1.5'
    },
    link: {
        color: '#27B05B',
        textDecoration: 'none'
    },
    successCard: {
        textAlign: 'center',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '60px 40px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        maxWidth: '500px',
        margin: '100px auto'
    },
    successTitle: {
        fontSize: '32px',
        fontWeight: '700',
        color: '#2C3E50',
        margin: '20px 0'
    },
    successMessage: {
        fontSize: '16px',
        color: '#666',
        marginBottom: '10px'
    },
    redirectMessage: {
        fontSize: '14px',
        color: '#999',
        fontStyle: 'italic'
    },
    spinner: {
        width: '20px',
        height: '20px',
        border: '3px solid rgba(255,255,255,0.3)',
        borderTop: '3px solid white',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    }
};

export default PaymentCheckout;
